import { usePathname } from 'next/navigation';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackPageLoad } from 'presentation/events/hooks/useTrackPageLoad';
import {
	SUBSCRIBE_EMAIL_STORAGE_KEY,
	SUBSCRIPTION_COMPLETE_PATH,
} from 'presentation/events/hooks/useTrackSubscribe';
import { waitForReactToBeIdle } from 'presentation/events/utils/ga4Utils';
import { useEffect, useRef } from 'react';

/**
 * `page_load` for non-PDP/BDP pages. PDP/BDP have their own `page_load` effect (in
 * `usePDPTrackLoadEvents`/`useBundleTrackLoadEvents`) since they need `sku`/`categories`.
 *
 * Unlike `page_view`/`subscribe` (in `useGlobalPageTrackEvents`), `page_load` stays tied to
 * the page actually finishing rendering (`waitForReactToBeIdle`) — it is the starter for
 * third-party pixel events.
 */
export const useTrackGlobalPageLoad = () => {
	const pathname = usePathname();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const trackPageLoad = useTrackPageLoad();

	const pageLoadTrackedKeyRef = useRef<string | null>(null);

	useEffect(() => {
		if (!pathname) return;

		const isPDP = pathname.startsWith('/p/');
		const isBDP = pathname.startsWith('/g/');
		if (isPDP || isBDP) return;

		const trackingKey = `${pathname}|${physicalStoreId}`;
		if (pageLoadTrackedKeyRef.current === trackingKey) return;

		let isMounted = true;

		waitForReactToBeIdle().then(() => {
			if (!isMounted) return;
			if (pageLoadTrackedKeyRef.current === trackingKey) return;

			pageLoadTrackedKeyRef.current = trackingKey;

			trackPageLoad();

			if (pathname.includes(SUBSCRIPTION_COMPLETE_PATH)) {
				localStorage.removeItem(SUBSCRIBE_EMAIL_STORAGE_KEY);
			}
		});

		return () => {
			isMounted = false;
		};
	}, [pathname, physicalStoreId, trackPageLoad]);
};
